import { Box, Dialog, DialogContent, Grid, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import {
	GridAlignment,
	GridCellParams,
	GridColDef,
	GridEventListener,
	GridRowEditStopReasons,
	GridRowId,
	GridRowModel,
	GridRowModes,
	GridRowModesModel
} from '@mui/x-data-grid';
import React, { useEffect, useState } from 'react';
import AppAccordion from '../../../../Component/Common/Components/Accordion/AppAccordion';
import StyledDataGrid from '../../../../Component/Common/Components/DataGrid/StyledDataGrid';
import { WorkforceProfileGoalsAndTargets } from '../../../../Component/Model/Dto/EmploymentEquity/WorkforceProfileGoalsAndTargets/WorkforceProfileGoalsAndTargets';
import { WorkforceProfileGoalsAndTargetsService } from '../../../../Component/Api/EmploymentEquity/WorkforceProfileGoalsAndTargets/WorkforceProfileGoalsAndTargetsService';
import { EEOccupationalLevel, WorkForceGoalAndTargetRow } from '../../../../Component/Model/Enum/EmploymentEquity';
import { WorkforceProfileOccupationalLevelTargets } from '../../../../Component/Model/Dto/EmploymentEquity/WorkforceProfileGoalsAndTargets/WorkforceProfileOccupationalLevelTargets';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import {
	CalculateGap,
	MapDeviationData,
	MapDrilldownData,
	MapHeadcountPercentagePairs,
	MapUpdatedRowToGoalsAndTargetRow
} from './OccupationalLevelGridMapper';
import { IGoalAndTargetRow } from '../../../Common/WorkforceProfile/GoalsAndTargetsCommon';
import { decimalFormatter } from '../../../../Helpers/DecimalFormatter';
import { toast } from 'react-toastify';
import { DemographicDeviationModal } from '../../Deviations/Justifications/Demographic/DemographicDeviationModal';
import { Gender, RaceTypes } from '../../../../Component/Model/Enum/BEE123Enums';
import { SmallLoading } from '../../../../Component/Common/Components/Loading/IntermediateLoading';
import { WorkforceCommentsNew } from './WorkforceCommentsNew';
import { CustomDialogTitle } from '../../../Common/Custom/CustomDialogTitle';
import { WorkForeProfileDrilldown } from './Drilldown/WorkForceProfileDrillDown';
import AppTextInput from '../../../../Component/Common/Components/FormValidationInputs/AppTextInput';
import { useForm } from 'react-hook-form';
import { ProjectedHeadcountModal } from './ProjectedHeadcount/ProjectedHeadcountModal';
import { DeviationsTableModal } from './Deviations/DeviationsTableModal';

interface IOccupationalLevelGridProps {
	title: string;
	occupationalLevel: EEOccupationalLevel;
	showPercentage: boolean;
	scorecardId: string;
	isDisabled: boolean;
	showSummary: boolean;
	date: Date;
	commentData: WorkforceProfileGoalsAndTargets.CommentsDtoOut[];
	onYearTargetChange: (yearId?: WorkForceGoalAndTargetRow) => void;
	refetchSummaryTarget?: number;
}
const WorkforceProfileOccupationalLevelDataGrid: React.FC<IOccupationalLevelGridProps> = (props) => {
	const [baselineTableRows, setBaselineTableRows] = useState<any[]>([]);
	const [eePlanYearTableRows, setEEPlanYearTableRows] = useState<any[]>([]);
	const [eeTargetTableRows, setEETargetYearTableRows] = useState<any[]>([]);
	const [rows, setRows] = React.useState(eePlanYearTableRows);

	const [rowModesModelYear1, setRowModesModelYear1] = React.useState<GridRowModesModel>({});
	const [rowModesModelYear2, setRowModesModelYear2] = React.useState<GridRowModesModel>({});
	const [rowModesModelYear3, setRowModesModelYear3] = React.useState<GridRowModesModel>({});
	const [rowModesModelYear4, setRowModesModelYear4] = React.useState<GridRowModesModel>({});
	const [rowModesModelYear5, setRowModesModelYear5] = React.useState<GridRowModesModel>({});

	const [selectedDeviationId, setSelectedDeviationId] = React.useState<number | undefined>();
	const [selectedRaceId, setSelectedRaceId] = React.useState<RaceTypes>(RaceTypes.African);
	const [selectedGenderId, setSelectedGenderId] = React.useState<Gender>(Gender.Male);
	const [selectedDeviationYear, setSelectedDeviationYear] = React.useState<number>(11);
	const [showDeviationModal, setShowDeviationModal] = React.useState<boolean>(false);
	const [showDeviationsTableModal, setShowDeviationsTableModal] = React.useState<boolean>(false);
	const [selectedHeadcountDate, setSelectedHeadcountDate] = React.useState<Date | undefined>();
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [baselineHeadcount, setBaselineHeadcount] = useState<number>(0);
	const [showDrillDown, setShowDrillDown] = React.useState<boolean>(false);
	const raceGenderPairKeys = [
		'African Male',
		'Coloured Male',
		'Indian Male',
		'White Male',
		'African Female',
		'Coloured Female',
		'Indian Female',
		'White Female',
		'Non South African Male',
		'Non South African Female'
	];
	const nonDesignatedDemographics = ['White Male', 'Non South African Male', 'Non South African Female'];
	const occupationalLevelsWithoutSectoralValues = [
		EEOccupationalLevel.Semi_Skilled,
		EEOccupationalLevel.Unskilled,
		EEOccupationalLevel.Temporary
	];
	const { control, setValue } = useForm();
	const [showProjectedHeadcountModal, setShowProjectedHeadcountModal] = React.useState<boolean>(false);
	const [selectedProjectedHeadcountId, setSelectedProjectedHeadcountId] = React.useState<number | undefined>();
	const [projectedHeadcount, setProjectedHeadcount] =
		React.useState<WorkforceProfileOccupationalLevelTargets.ProjectedHeadcount>();
	const isDisabledEmployeesOccupationalLevelSection = props.isDisabled && !props.showSummary;

	useEffect(() => {
		fetchData();
	}, [props.scorecardId, props.occupationalLevel, props.isDisabled, props.date]);

	useEffect(() => {
		if (props.refetchSummaryTarget) {
			fetchEEPlanYearData(props.refetchSummaryTarget);
			props.onYearTargetChange(undefined);
		}
	}, [props.refetchSummaryTarget]);

	const handleCloseDeviationModal = () => {
		setShowDeviationModal(false);
		setSelectedDeviationId(undefined);
		fetchEEPlanYearData(selectedDeviationYear);
	};

    const handleCloseDeviationsTableModal = () => {
		setShowDeviationsTableModal(false);
		fetchEEPlanYearData(selectedDeviationYear);
	};

	const handleCloseProjectedHeadcountModal = () => {
		setShowProjectedHeadcountModal(false);
		setSelectedProjectedHeadcountId(undefined);
		fetchEEPlanYearData(selectedDeviationYear);
		fetchData();
	};

	const fetchData = async () => {
		try {
			setIsLoading(true);
			const occupationalLevelData = props.showSummary
				? await WorkforceProfileGoalsAndTargetsService.GetDisabledSummary(props.scorecardId, props.date)
				: await WorkforceProfileGoalsAndTargetsService.GetOccupationalLevelTables(
						props.scorecardId,
						props.occupationalLevel,
						props.isDisabled,
						props.date
				  );
			const data = occupationalLevelData.data;
			setProjectedHeadcount(data.projectedHeadcount);
			setValue('projectedHeadcountValue', data?.projectedHeadcount?.projectedHeadcountValue);

			const yearMapping = data.years.map((m) => {
				return getEEYearTableRows(m);
			});
			setEEPlanYearTableRows(yearMapping);
			
			if (!isDisabledEmployeesOccupationalLevelSection) {
				setBaselineHeadcount(data.baseSnapshot.totalHeadcount);
				setBaselineTableRows(getBaselineTableRows(data));
			}

			if (props.isDisabled) {
				const originalOccpationalLevelData = await WorkforceProfileGoalsAndTargetsService.GetOccupationalLevelTables(
					props.scorecardId,
					props.occupationalLevel,
					false,
					props.date
				);
				const originalData = originalOccpationalLevelData.data;
				const originalMapping = originalData.years.map((m) => {
					return getEETargetTableRow(m);
				});
				setEETargetYearTableRows(originalMapping);
			}
		} catch (error) {
			console.error('Error fetching data:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const fetchEEPlanYearData = async (yearId: WorkForceGoalAndTargetRow) => {
		try {
			const occpationalLevelData = props.showSummary
				? await WorkforceProfileGoalsAndTargetsService.GetDisabledSummary(props.scorecardId, props.date)
				: await WorkforceProfileGoalsAndTargetsService.GetOccupationalLevelTables(
						props.scorecardId,
						props.occupationalLevel,
						props.isDisabled,
						props.date
				  );

			const data = occpationalLevelData.data;
			const yearMapping = data.years.map((m, index) => {
				if (m.targetRow == yearId) {
					return getEEYearTableRows(m);
				} else {
					return eePlanYearTableRows[index];
				}
			});
			setEEPlanYearTableRows(yearMapping);
		} catch (error) {
			console.error('Error fetching data:', error);
		}
	};

	const getBaselineTableRows = (data: WorkforceProfileOccupationalLevelTargets.OccupationalLevelSection) => {
		const mappedDrilldownBaseSnapShotData = MapDrilldownData(data.baseSnapshot.headcountPercentagePairs);
		const mappedBaseSnapshotData = MapHeadcountPercentagePairs(data.baseSnapshot.headcountPercentagePairs, raceGenderPairKeys);
		const mappedSectoralData = MapHeadcountPercentagePairs(data.sectoralTarget.headcountPercentagePairs, raceGenderPairKeys);
		const mappedEAPData = MapHeadcountPercentagePairs(data.eapTarget?.headcountPercentagePairs, raceGenderPairKeys);

		const sectoralGap = CalculateGap(mappedBaseSnapshotData, mappedSectoralData, raceGenderPairKeys, true);
		const eapGap = CalculateGap(mappedBaseSnapshotData, mappedEAPData, raceGenderPairKeys, true);

		let rows = [
			{ id: 'baseSnapshotSpacer', description: 'Snapshot', isSpacer: true, isNumber: true },
			{
				id: 1,
				description: `Employees -\n${data.baseSnapshot.abbreviatedDate}`,
				...mappedDrilldownBaseSnapShotData,
				headCountDate: data.baseSnapshot.headcountDate,
				totalHeadcount: data.baseSnapshot.totalHeadcount,
				totalHeadcountPercentage: data.baseSnapshot.totalPercentage,
				isNumber: true,
				isDrillDownRow: true
			},
			{ id: 'SectoralTargetSpacer', description: 'Sectoral', isSpacer: true, isNumber: false, isSectoral: true },
			{
				id: 2,
				description: data.sectoralTarget.description,
				...mappedSectoralData,
				totalHeadcount: props.showSummary ? data.sectoralTarget.totalHeadcount : '',
				totalHeadcountPercentage: props.showSummary ? data.sectoralTarget.totalPercentage : '',
				isNumber: false,
				canBeNA: true,
				isSectoral: true
			},
			{
				id: 3,
				description: 'GAP',
				...sectoralGap,
				totalHeadcount: props.showSummary ? data.baseSnapshot.totalHeadcount - data.sectoralTarget.totalHeadcount : '',
				totalHeadcountPercentage: props.showSummary
					? (data.baseSnapshot.totalPercentage ?? 0) - (data.sectoralTarget?.totalPercentage ?? 0)
					: '',
				isNumber: false,
				isCalculatedRow: true,
				canBeNA: true,
				isSectoral: true
			},
			{ id: 'eapSpacer', description: 'EAP', isSpacer: true, isNumber: false, isEAPValue: true },
			{
				id: 4,
				description: data.eapTarget?.description,
				...mappedEAPData,
				isNumber: false,
				canBeNA: true,
				isEAPValue: true
			},
			{
				id: 5,
				description: 'GAP',
				...eapGap,
				isNumber: false,
				isCalculatedRow: true,
				canBeNA: true,
				isEAPValue: true
			}
		];

		if (occupationalLevelsWithoutSectoralValues.includes(props.occupationalLevel)) {
			rows = rows.filter((x) => !x.isSectoral);
		}

		if (props.showSummary) {
			rows = rows.filter((x) => !x.isEAPValue);
		}
		return rows;
	};
	const getEETargetTableRow = (yearData: WorkforceProfileOccupationalLevelTargets.OccupationalLevelYearSubSection) => {
		const targetYear = yearData.targetHeadcountPercentagePairs;
		const targetYearData = MapHeadcountPercentagePairs(targetYear, raceGenderPairKeys);
		const tableRows = {
			...targetYearData,
			TargetRow: yearData.targetRow
		};

		return tableRows;
	};

	const getEEYearTableRows = (yearData: WorkforceProfileOccupationalLevelTargets.OccupationalLevelYearSubSection) => {
		const targetYear = yearData.targetHeadcountPercentagePairs;
		const actualYear = yearData.headcountPercentagePairs;

		const targetYearData = MapHeadcountPercentagePairs(targetYear, raceGenderPairKeys);
		const mappedDrilldownactualYearData = MapDrilldownData(actualYear);
		const actualYearData = MapHeadcountPercentagePairs(actualYear, raceGenderPairKeys);

		const varianceData = CalculateGap(actualYearData, targetYearData, raceGenderPairKeys);
		const deviationRowData = MapDeviationData(targetYear, varianceData);

		const headcountGap = yearData.totalHeadcount - yearData.totalTargetHeadcount;
		let percentageGap: number | string;

		percentageGap =
			yearData.totalTargetHeadcount > 0
				? (headcountGap / yearData.totalTargetHeadcount) * 100
				: yearData.totalHeadcount > 0
				? 'N/A'
				: 0;

		let tableRows = [
			{
				id: 1,
				description: 'Target',
				...targetYearData,
				totalHeadcount: yearData.totalTargetHeadcount,
				totalHeadcountPercentage: yearData.totalTargetPercentage,
				TargetRow: yearData.targetRow,
				isTargetRow: true,
				TableHeading: yearData.description,
				isNumber: true,
				isDeviationRow: false,
				isDeviationCaptured: yearData.isDeviationCaptured
			},
			{
				id: 2,
				description: `Employees -\n${yearData.abbreviatedDate}`,
				...mappedDrilldownactualYearData,
				headCountDate: yearData.headcountDate,
				totalHeadcount: yearData.totalHeadcount,
				totalHeadcountPercentage: yearData.totalPercentage,
				isNumber: true,
				isDrillDownRow: true,
				isDeviationRow: false
			},
			{
				id: 3,
				description: 'Variance',
				...varianceData,
				totalHeadcount: props.showSummary ? headcountGap : '',
				totalHeadcountPercentage: props.showSummary ? percentageGap : '',
				isVarianceRow: true,
				isNumber: false,
				isCalculatedRow: true,
				isDeviationRow: false
			},
			{
				id: 4,
				description: 'Deviations',
				...deviationRowData,
				totalHeadcount: '',
				totalHeadcountPercentage: '',
				TargetRow: yearData.targetRow,
				isDeviationRow: true,
				isNumber: false
			}
		];

		if (props.showSummary) {
			tableRows = tableRows.filter((x) => !x.isDeviationRow);
		}

		return tableRows;
	};

	const columnGroupingModel = [
		{
			groupId: 'AM',
			description: 'African Male',
			children: [{ field: 'African MaleHeadcount' }, { field: 'African MalePercentage' }]
		},
		{
			groupId: 'AF',
			description: 'African Female',
			children: [{ field: 'African FemaleHeadcount' }, { field: 'African FemalePercentage' }]
		},
		{
			groupId: 'CM',
			description: 'Coloured Male',
			children: [{ field: 'Coloured MaleHeadcount' }, { field: 'Coloured MalePercentage' }]
		},
		{
			groupId: 'IM',
			description: 'Indian Male',
			children: [{ field: 'Indian MaleHeadcount' }, { field: 'Indian MalePercentage' }]
		},
		{
			groupId: 'WM',
			description: 'White Male',
			children: [{ field: 'White MaleHeadcount' }, { field: 'White MalePercentage' }]
		},
		{
			groupId: 'CF',
			description: 'Coloured Female',
			children: [{ field: 'Coloured FemaleHeadcount' }, { field: 'Coloured FemalePercentage' }]
		},
		{
			groupId: 'IF',
			description: 'Indian Female',
			children: [{ field: 'Indian FemaleHeadcount' }, { field: 'Indian FemalePercentage' }]
		},
		{
			groupId: 'WF',
			description: 'White Female',
			children: [{ field: 'White FemaleHeadcount' }, { field: 'White FemalePercentage' }]
		},
		{
			groupId: 'FNM',
			description: 'Foreign National Male',
			children: [{ field: 'Non South African MaleHeadcount' }, { field: 'Non South African MalePercentage' }]
		},
		{
			groupId: 'FNF',
			description: 'Foreign National Female',
			children: [{ field: 'Non South African FemaleHeadcount' }, { field: 'Non South African FemalePercentage' }]
		},
		{
			groupId: 'Total',
			children: [{ field: 'totalHeadcount' }]
		}
	];

	const getCellClassName = (params: GridCellParams, category: string, cellClass: string) => {
		if (params.row.isCalculatedRow) {
			const varianceClass =
				Number(params.value) < 0 ? 'negative-variance' : Number(params.value) === 0 ? 'zero-variance' : 'positive-variance';
			const isEAPValue = params.row.isEAPValue;
			const isNonDesignatedCategory = !nonDesignatedDemographics.includes(category);
			const isOccupationalLevelValid = occupationalLevelsWithoutSectoralValues.includes(props.occupationalLevel);

			if (
				!params.row.canBeNA ||
				(isNonDesignatedCategory && !isEAPValue) ||
				(isNonDesignatedCategory && isOccupationalLevelValid && isEAPValue) ||
				(isEAPValue && category === 'White Male')
			) {
				return varianceClass;
			}
		}
		return params.row.isSpacer ? '' : cellClass;
	};

	const columns = (includeActionsColumn: boolean, tableIndex?: number): GridColDef[] => [
		{
			field: 'description',
			headerName: 'Description',
			width: 120,
			disableColumnMenu: true,
			sortable: false,
			headerAlign: 'left',
			headerClassName: 'hideRightSeparator',
			cellClassName: 'left-align-cell',
			renderCell: (params) => {
				const description = params.value || '';
				const lines = description.split('\n');
				if (lines) {
					return (
						<div style={{ whiteSpace: 'pre-line' }}>
							{lines.map((line: string, index: number) => (
								<div key={index}>{line}</div>
							))}
						</div>
					);
				}
			}
		},
		...raceGenderPairKeys.flatMap((category: string, index: number) => {
			const isEvenIndex = index % 2 === 0;
			const cellClass = isEvenIndex ? 'greyed-out-cell' : '';

			const fields: GridColDef[] = !props.showSummary
				? [
						{
							field: `${category}Headcount`,
							headerName: 'Head\ncount',
							minWidth: 60,
							flex: 0.4,
							disableColumnMenu: true,
							sortable: false,
							headerAlign: 'center' as GridAlignment,
							editable: includeActionsColumn,
							type: 'number',
							headerClassName: (params) => {
								return props.showPercentage ? '' : 'hideRightSeparator';
							},
							valueParser: (value) => {
								let parsedValue = Number(value);
								if (parsedValue < 0) {
									toast.warning('Target Employees must be at least 0');
									parsedValue = 0;
								} else if (parsedValue > 10000000) {
									toast.warning('Target Employees must not be greater than 10 000 000');
									parsedValue = 10000000;
								}
								return parsedValue;
							},
							colSpan: (params): number | undefined => {
								if ((params.row.isDeviationRow ?? false) && props.showPercentage) {
									return 2;
								} else {
									return 1;
								}
							},
							cellClassName: (params) => {
								return getCellClassName(params, category, cellClass);
							},
							renderCell: (params) => {
								if (params.row.isSpacer) {
									var value = params.value;
									value = '';
									return value;
								} else if (params.row.isDeviationRow) {
									if (params.value?.showDeviation) {
										if (params.value?.deviationId == null) {
											return (
												<Tooltip key='addDeviation' title='Add Deviation'>
													<IconButton
														onClick={() => {
															setSelectedDeviationId(undefined);
															setShowDeviationModal(true);
															setSelectedRaceId(params.value.race);
															setSelectedGenderId(params.value.gender);
															setSelectedDeviationYear(params.row.TargetRow);
														}}
														sx={{ padding: 0.1 }}
														color='secondary'
													>
														<AddCircleOutlineIcon />
													</IconButton>
												</Tooltip>
											);
										} else {
											return (
												<Tooltip key='editDeviation' title='Edit Deviation'>
													<IconButton
														onClick={() => {
															setSelectedDeviationId(params.value?.deviationId);
															setShowDeviationModal(true);
															setSelectedRaceId(params.value.race);
															setSelectedGenderId(params.value.gender);
															setSelectedDeviationYear(params.row.TargetRow);
														}}
														sx={{ padding: 0.1 }}
														color='secondary'
													>
														<EditIcon />
													</IconButton>
												</Tooltip>
											);
										}
									} else {
										return '-';
									}
								} else if (projectedHeadcount == null && (params.row.isEAPValue || params.row.isSectoral)) {
									return 'N/A';
								} else if (params.row.canBeNA && nonDesignatedDemographics.includes(category)) {
									if (params.row.isEAPValue && category == 'White Male') {
										const formattedValue = decimalFormatter(params.value, 1);
										if (params.row.isCalculatedRow) {
											return params.value > 0 ? '+' + formattedValue : formattedValue;
										}
										return formattedValue;
									} else {
										return 'N/A';
									}
								} else if (params.value !== undefined && params.row.isNumber) {
									if (params.row.isDrillDownRow) {
										return (
											<span
												onClick={() => {
													setShowDrillDown(true);
													setSelectedRaceId(params.value.race);
													setSelectedGenderId(params.value.gender);
													setSelectedHeadcountDate(params.row.headCountDate);
												}}
												className='clickable-cell'
											>
												{decimalFormatter(params.value.headcount, 0)}
											</span>
										);
									} else {
										return decimalFormatter(params.value, 0);
									}
								} else if (params.value !== undefined && !params.row.isNumber) {
									const formattedValue = params.row.isVarianceRow ? params.value : decimalFormatter(params.value, 1);

									if (params.row.isCalculatedRow) {
										return params.value > 0 ? '+' + formattedValue : formattedValue;
									}
									return formattedValue;
								} else {
									return 0;
								}
							}
						}
				  ]
				: [];

			if (props.showPercentage && !props.showSummary) {
				fields.push({
					field: `${category}Percentage`,
					headerName: '%',
					minWidth: 60,
					flex: 0.4,
					disableColumnMenu: true,
					sortable: false,
					headerAlign: 'center' as GridAlignment,
					type: 'number',
					headerClassName: 'hideRightSeparator',
					cellClassName: (params) => {
						return getCellClassName(params, category, cellClass);
					},
					renderCell: (params) => {
						if (params.row.isSpacer) {
							var value = params.value;
							value = '';
							return value;
						} else if (params.row.canBeNA && nonDesignatedDemographics.includes(category)) {
							if (params.row.isEAPValue && category == 'White Male') {
								return decimalFormatter(params.value, 2);
							} else if (params.row.isVarianceRow && !params.row.isNumber && params.row.isCalculatedRow) {
								return 'N/A';
							} else {
								return 'N/A';
							}
						} else if (params.row.canBeNA) {
							return decimalFormatter(params.value, 2);
						} else if (params.value !== undefined && params.row.isNumber) {
							if (params.row.isDrillDownRow) {
								return (
									<span
										onClick={() => {
											setShowDrillDown(true);
											setSelectedRaceId(params.value.race);
											setSelectedGenderId(params.value.gender);
											setSelectedHeadcountDate(params.row.headCountDate);
										}}
										className='clickable-cell'
									>
										{decimalFormatter(params.value.percentage, 2)}
									</span>
								);
							} else {
								return decimalFormatter(params.value, 2);
							}
						} else if (params.value !== undefined && !params.row.isNumber) {
							const originalValue = params.value;
							if (params.row.isCalculatedRow && params.value != 'N/A') {
								const formattedValue = decimalFormatter(originalValue, 2);
								return params.value > 0 ? '+' + formattedValue : formattedValue;
							}
							return originalValue;
						} else {
							return 'N/A';
						}
					}
				});
			}

			return fields;
		}),
		{
			field: 'totalHeadcount',
			headerName: !props.showSummary ? 'Head\ncount' : 'Total\nHeadcount',
			minWidth: 60,
			flex: 0.4,
			disableColumnMenu: true,
			sortable: false,
			headerAlign: 'center',
			headerClassName: 'hideRightSeparator',
			cellClassName: (params) => {
				if (props.showSummary) {
					return getCellClassName(params, '', '');
				} else {
					return params.row.isSpacer ? '' : 'greyed-out-cell';
				}
			},
			renderCell: (params: any) => {
				const originalValue = params.value;
				if (props.showSummary) {
					if (projectedHeadcount == null && !params.row.isSpacer && params.row.isSectoral) {
						return 'N/A';
					} else if (params.row.isCalculatedRow && params.value != 'N/A') {
						const formattedValue = params.row.isVarianceRow ? params.value : decimalFormatter(params.value, 1);

						return params.value > 0 ? '+' + formattedValue : formattedValue;
					}
				}
				return originalValue;
			}
		},
		...(props.showSummary
			? [
					{
						field: 'totalHeadcountPercentage',
						headerName: 'Total\nPercentage',
						minWidth: 60,
						flex: 0.4,
						disableColumnMenu: true,
						sortable: false,
						headerAlign: 'center' as GridAlignment,
						headerClassName: 'hideRightSeparator',
						cellClassName: (params: any) => {
							return getCellClassName(params, '', 'greyed-out-cell');
						},
						renderCell: (params: any) => {
							const originalValue = params.value;
							if (params.row.isCalculatedRow && params.value != 'N/A') {
								const formattedValue = decimalFormatter(originalValue, 2);
								return params.value > 0 ? '+' + formattedValue : formattedValue;
							}
							return decimalFormatter(originalValue, 2);
						}
					}
			  ]
			: []),
		...(includeActionsColumn && !props.showSummary
			? [
					{
						field: 'actions',
						headerName: '',
						width: 60,
						flex: 0.4,
						sortable: false,
						filterable: false,
						disableColumnMenu: true,
						type: 'number',
						headerAlign: 'center' as GridAlignment,
						renderCell: (params: any) => {
							const rowModesModel =
								tableIndex === 0
									? rowModesModelYear1
									: tableIndex === 1
									? rowModesModelYear2
									: tableIndex === 2
									? rowModesModelYear3
									: tableIndex === 3
									? rowModesModelYear4
									: rowModesModelYear5;

							const isInEditMode = rowModesModel[params.row.id]?.mode === GridRowModes.Edit;

							if (params.row.description !== null && params.row.isTargetRow) {
								if (isInEditMode) {
									return [
										<Tooltip key='save' title='Save'>
											<IconButton
												onClick={handleSaveClick(params.row.id, tableIndex ?? 0)}
												sx={{ padding: 0.1 }}
												color='secondary'
											>
												<SaveIcon />
											</IconButton>
										</Tooltip>,
										<Tooltip key='cancel' title='Cancel'>
											<IconButton onClick={handleCancelClick(params.row.id, tableIndex ?? 0)} sx={{ padding: 0.1 }} color='error'>
												<CancelIcon />
											</IconButton>
										</Tooltip>
									];
								}
								return [
									<Tooltip key='edit' title='Edit'>
										<IconButton onClick={handleEditClick(params.row.id, tableIndex ?? 0)} sx={{ padding: 0.1 }} color='secondary'>
											<EditIcon />
										</IconButton>
									</Tooltip>
								];
							}
							return null;
						}
					}
			  ]
			: [])
	];

	const handleEditClick = (id: GridRowId, tableIndex: number) => () => {
		switch (tableIndex) {
			case 0:
				setRowModesModelYear1({ ...rowModesModelYear1, [id]: { mode: GridRowModes.Edit } });
				break;
			case 1:
				setRowModesModelYear2({ ...rowModesModelYear2, [id]: { mode: GridRowModes.Edit } });
				break;
			case 2:
				setRowModesModelYear3({ ...rowModesModelYear3, [id]: { mode: GridRowModes.Edit } });
				break;
			case 3:
				setRowModesModelYear4({ ...rowModesModelYear4, [id]: { mode: GridRowModes.Edit } });
				break;
			case 4:
				setRowModesModelYear5({ ...rowModesModelYear5, [id]: { mode: GridRowModes.Edit } });
				break;
		}
	};

	const handleCancelClick = (id: GridRowId, tableIndex: number) => () => {
		switch (tableIndex) {
			case 0:
				setRowModesModelYear1({
					...rowModesModelYear1,
					[id]: { mode: GridRowModes.View, ignoreModifications: true }
				});
				break;
			case 1:
				setRowModesModelYear2({
					...rowModesModelYear2,
					[id]: { mode: GridRowModes.View, ignoreModifications: true }
				});
				break;
			case 2:
				setRowModesModelYear3({
					...rowModesModelYear3,
					[id]: { mode: GridRowModes.View, ignoreModifications: true }
				});
				break;
			case 3:
				setRowModesModelYear4({
					...rowModesModelYear4,
					[id]: { mode: GridRowModes.View, ignoreModifications: true }
				});
				break;
			case 4:
				setRowModesModelYear5({
					...rowModesModelYear5,
					[id]: { mode: GridRowModes.View, ignoreModifications: true }
				});
				break;
		}
	};

	const handleSaveClick = (id: GridRowId, tableIndex: number) => () => {
		switch (tableIndex) {
			case 0:
				setRowModesModelYear1({ ...rowModesModelYear1, [id]: { mode: GridRowModes.View } });
				break;
			case 1:
				setRowModesModelYear2({ ...rowModesModelYear2, [id]: { mode: GridRowModes.View } });
				break;
			case 2:
				setRowModesModelYear3({ ...rowModesModelYear3, [id]: { mode: GridRowModes.View } });
				break;
			case 3:
				setRowModesModelYear4({ ...rowModesModelYear4, [id]: { mode: GridRowModes.View } });
				break;
			case 4:
				setRowModesModelYear5({ ...rowModesModelYear5, [id]: { mode: GridRowModes.View } });
				break;
		}
	};

	const handleRowModesModelChange = (newRowModesModel: GridRowModesModel, tableIndex: number) => {
		switch (tableIndex) {
			case 0:
				setRowModesModelYear1(newRowModesModel);
				break;
			case 1:
				setRowModesModelYear2(newRowModesModel);
				break;
			case 2:
				setRowModesModelYear3(newRowModesModel);
				break;
			case 3:
				setRowModesModelYear4(newRowModesModel);
				break;
			case 4:
				setRowModesModelYear5(newRowModesModel);
				break;
		}
	};

	const processRowUpdate = async (newRow: GridRowModel, oldRow: GridRowModel) => {
		if (props.isDisabled) {
			let errorMessage: boolean = false;
			const originalMapped = eeTargetTableRows.filter((f) => f.TargetRow == newRow.TargetRow)[0];
			for (const key of Object.keys(originalMapped).filter((k) => k.includes('Headcount'))) {
				const originalValue = originalMapped[key];
				const newValue = newRow[key];

				if (newValue > originalValue) {
					const displayKey = key.replace('Headcount', '').trim();
					toast.error(`Disabled ${displayKey} is greater than Total ${displayKey}`);
					errorMessage = true;
				}
			}
			if (errorMessage) return oldRow;
		}

		const updatedRow = { ...newRow };
		setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
		await saveRow(updatedRow);

		return updatedRow;
	};

	const saveRow = async (updatedRow: any) => {
		try {
			const mappedRow: IGoalAndTargetRow = MapUpdatedRowToGoalsAndTargetRow({
				...updatedRow,
				scorecardId: props.scorecardId,
				occupationalLevel: props.occupationalLevel,
				isDisabled: props.isDisabled
			});

			await WorkforceProfileGoalsAndTargetsService.SaveWorkforceProfileRow(mappedRow);
			fetchEEPlanYearData(mappedRow.id);

			if (props.isDisabled) {
				props.onYearTargetChange(mappedRow.id);
			}
		} catch (error: any) {
			console.log(error);
		}
	};

	const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
		if (params.reason === GridRowEditStopReasons.rowFocusOut) {
			event.defaultMuiPrevented = true;
		}
	};

	const onProjectedHeadcountClick = () => {
		setShowProjectedHeadcountModal(true);
		setSelectedProjectedHeadcountId(projectedHeadcount?.id);
	};

	return (
		<AppAccordion expandOnDefault={false} label={props.title}>
			{isLoading ? (
				<SmallLoading></SmallLoading>
			) : (
				<>
					{!isDisabledEmployeesOccupationalLevelSection && (
						<>
							<Grid container direction='row' style={{ width: '100%', overflow: 'hidden' }}>
								{props.showSummary && (
									<Grid item xs={12} sx={{ mt: 1, mb: 1 }}>
										<Typography variant='body2' fontSize='0.7em' style={{ fontStyle: 'italic' }}>
											Note that <b>employees</b> in this section refers to <b>all designated disabled employees</b> and is defined
											as all disabled employees, regardless of occupational level, excluding Foreign National Males and Foreign
											National Females
										</Typography>
									</Grid>
								)}
								<Grid item xs={12}>
									<Typography variant='body1'>Baseline Year</Typography>
									<Typography variant='body2' fontSize='0.7em'>
										Regulation requires that you set targets based on your projected headcount in 2030. Please capture your
										projected headcount across this occupational level so that the system can calculate the sectoral and EAP
										target headcount values.
									</Typography>

									<Grid item xs={12} mt={1}>
										<Grid container spacing={1} alignItems='center'>
											<Grid item>
												<Typography variant='body2' fontWeight='bold'>
													Projected headcount at 31 August 2030
												</Typography>
											</Grid>

											<Grid item xs={1} sx={{ mb: 1 }}>
												<AppTextInput
													label=''
													name='projectedHeadcountValue'
													control={control}
													disabled={false}
													InputProps={{
														inputProps: {
															readOnly: true,
															style: { pointerEvents: 'none', fontWeight: 'bold' },
															tabIndex: -1
														}
													}}
												/>
											</Grid>

											{!props.showSummary && (
												<Grid item>
													{projectedHeadcount != null ? (
														<IconButton onClick={() => onProjectedHeadcountClick()} sx={{ padding: 0.1 }} color='info'>
															<EditIcon />
														</IconButton>
													) : (
														<IconButton onClick={() => onProjectedHeadcountClick()} sx={{ padding: 0.1 }} color='secondary'>
															<AddCircleOutlineIcon />
														</IconButton>
													)}
												</Grid>
											)}
										</Grid>
									</Grid>
								</Grid>
								<Grid item xs={12}>
									<StyledDataGrid
										rows={baselineTableRows}
										columns={columns(false)}
										disableSelectionOnClick
										hideFooter={true}
										experimentalFeatures={{ columnGrouping: true }}
										columnGroupingModel={!props.showSummary ? columnGroupingModel : undefined}
										getRowClassName={(params) => (params.row.isSpacer ? 'spacer-row' : '')}
										getRowHeight={() => 'auto'}
										autoHeight={true}
									/>
								</Grid>
							</Grid>

							<AppAccordion
								expandOnDefault={false}
								label={'EEA12 Comments for ' + props.title}
								backgroundColor={'#9EB8C2'}
								textColor={'black'}
								fontSize='14px'
								height='10px'
								iconFill={'black'}
							>
								<WorkforceCommentsNew
									comments={props.commentData}
									occupationalLevel={props.occupationalLevel}
									scorecardId={props.scorecardId}
									isDisabled={props.isDisabled}
								/>
							</AppAccordion>
							<br></br>
						</>
					)}
					<Typography variant='body1'>EE Plan</Typography>

					{eePlanYearTableRows.map((yearData, index) => {
						return (
							<AppAccordion expandOnDefault={false} label={yearData[0].TableHeading} opacity={'75%'}>
								<Grid container width='100%'>
									<Grid item xs={12} xl={12}>
										<StyledDataGrid
											rows={yearData}
											columns={columns(true, index)}
											disableSelectionOnClick
											hideFooter={true}
											experimentalFeatures={{ columnGrouping: true, newEditingApi: true }}
											columnGroupingModel={!props.showSummary ? columnGroupingModel : undefined}
											getRowClassName={(params) => (params.row.isSpacer ? 'spacer-row' : '')}
											getRowHeight={() => 'auto'}
											autoHeight={true}
											onRowEditStop={handleRowEditStop}
											isCellEditable={(params) => {
												switch (index) {
													case 0:
														return rowModesModelYear1[params.row.id]?.mode === GridRowModes.Edit;
													case 1:
														return rowModesModelYear2[params.row.id]?.mode === GridRowModes.Edit;
													case 2:
														return rowModesModelYear3[params.row.id]?.mode === GridRowModes.Edit;
													case 3:
														return rowModesModelYear4[params.row.id]?.mode === GridRowModes.Edit;
													case 4:
														return rowModesModelYear5[params.row.id]?.mode === GridRowModes.Edit;
													default:
														return false;
												}
											}}
											rowModesModel={
												index === 0
													? rowModesModelYear1
													: index === 1
													? rowModesModelYear2
													: index === 2
													? rowModesModelYear3
													: index === 3
													? rowModesModelYear4
													: rowModesModelYear5
											}
											onRowModesModelChange={(newModel) => handleRowModesModelChange(newModel, index)}
											processRowUpdate={processRowUpdate}
											editMode='row'
											onCellKeyDown={(params, event) => {
												if (event.key === '.' || event.key === ',') {
													event.preventDefault();
												}
											}}
										/>
									</Grid>
                                    {(!isDisabledEmployeesOccupationalLevelSection && props.occupationalLevel != EEOccupationalLevel.Temporary) && 
                                        <Grid item xs={12} xl={12}>
                                            <Box sx={{ backgroundColor: '#9EB8C2', color: '#000000', height: '48px' }}>
                                                <IconButton
                                                    onClick={() => {
                                                        setSelectedDeviationYear(yearData[0].TargetRow);
                                                        setShowDeviationsTableModal(true);
                                                    }}
                                                    sx={{ height: '100%' }}
                                                    color='inherit'
                                                >
                                                    {yearData[0].isDeviationCaptured ? <EditIcon /> : <AddCircleOutlineIcon />}
                                                    <Typography sx={{ fontSize: '14px' }} pl={0.5}>
                                                        {yearData[0].isDeviationCaptured ? 'Manage' : 'Capture'} Deviations for {yearData[0].TableHeading}
                                                    </Typography>
                                                </IconButton>
                                            </Box>
                                        </Grid>
                                    }
								</Grid>
							</AppAccordion>
						);
					})}

					{showDeviationModal && (
						<DemographicDeviationModal
							showModal={showDeviationModal}
							handleCancel={handleCloseDeviationModal}
							id={selectedDeviationId}
							occupationalLevelType={props.occupationalLevel as number}
							defaultRaceId={selectedRaceId as number}
							defaultGenderId={selectedGenderId as number}
							isDisabled={props.isDisabled}
							year={selectedDeviationYear}
							scorecardId={props.scorecardId}
						/>
					)}

					{showProjectedHeadcountModal && (
						<ProjectedHeadcountModal
							handleCancel={handleCloseProjectedHeadcountModal}
							id={selectedProjectedHeadcountId}
							occupationalLevel={props.occupationalLevel}
							occupationalLevelTitle={props.title}
							isDisabled={props.isDisabled}
							scorecardId={props.scorecardId}
							baselineHeadcount={baselineHeadcount} 
						/>
					)}
					{showDeviationsTableModal && (
						<DeviationsTableModal
							showModal={showDeviationsTableModal}
							handleCancel={handleCloseDeviationsTableModal}
							occupationalLevel={props.occupationalLevel}
							isDisabled={props.isDisabled}
							year={selectedDeviationYear}
							scorecardId={props.scorecardId}
						/>
					)}

					<Dialog
						open={showDrillDown}
						onClose={() => setShowDrillDown(false)}
						aria-labelledby='form-dialog-title'
						maxWidth={false}
						sx={{ minWidth: '1200px' }}
					>
						<CustomDialogTitle id='customized-dialog-title' onClose={() => setShowDrillDown(false)}>
							Employee Structure
						</CustomDialogTitle>
						<DialogContent>
							<WorkForeProfileDrilldown
								scorecardId={props.scorecardId}
								race={selectedRaceId}
								gender={selectedGenderId}
								occupationalLevel={props.occupationalLevel}
								isDisabled={props.isDisabled}
								date={selectedHeadcountDate}
							/>
						</DialogContent>
					</Dialog>
				</>
			)}
		</AppAccordion>
	);
};

export default WorkforceProfileOccupationalLevelDataGrid;
